import { useState } from 'react';
import type { Order } from '@/lib/supabase';

interface NewOrderAlertProps {
  order: Order;
  onAccept: (order: Order) => void;
  onReject: (order: Order, reason: string) => void;
  onSilence?: () => void;
}

const REJECT_REASONS = [
  'Item out of stock',
  'Delivery not available in this area',
  'Customer unreachable',
  'Suspicious order',
  'Order duplicate',
];

export default function NewOrderAlert({ order, onAccept, onReject, onSilence }: NewOrderAlertProps) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [error, setError] = useState('');
  const [silenced, setSilenced] = useState(false);

  const finalReason = rejectReason === 'custom' ? customReason.trim() : rejectReason;

  const handleReject = () => {
    if (!finalReason) {
      setError('Please select or enter a reason to reject the order.');
      return;
    }
    onReject(order, finalReason);
  };

  const handleSilence = () => {
    setSilenced(true);
    if (onSilence) onSilence();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Animated overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-pulse-slow" />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-bounce-in">
        {/* Top alert bar */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0 animate-ping-slow">
            <i className="ri-notification-3-fill text-white text-xl"></i>
          </div>
          <div>
            <p className="text-white font-bold text-base leading-none">New Order!</p>
            <p className="text-white/80 text-xs mt-0.5">Waiting for your action</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="text-right">
              <p className="text-white font-bold text-lg">₹{order.final_amount}</p>
              <p className="text-white/70 text-xs">COD</p>
            </div>
            {/* Silence / Mute Ring Button */}
            <button
              onClick={handleSilence}
              title="Ring band karo"
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                silenced
                  ? 'bg-white/30 text-white/60'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
            >
              <i className={silenced ? 'ri-volume-mute-fill text-lg' : 'ri-volume-mute-line text-lg'}></i>
            </button>
          </div>
        </div>

        {/* Silenced indicator */}
        {silenced && (
          <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
            <i className="ri-volume-mute-fill text-gray-400 text-sm"></i>
            <p className="text-gray-500 text-xs font-medium">Ring band kar di — order abhi bhi pending hai</p>
          </div>
        )}

        <div className="p-5">
          {/* Customer Info */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
              <i className="ri-user-3-line text-gray-600"></i>
            </div>
            <div>
              <p className="text-gray-900 font-bold text-sm">{order.customer_name}</p>
              <p className="text-gray-500 text-xs flex items-center gap-1">
                <i className="ri-phone-line"></i> {order.customer_phone}
              </p>
              <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
                <i className="ri-map-pin-line"></i> {order.customer_city}
              </p>
            </div>
          </div>

          {/* Items — full detail cards */}
          <div className="mb-4 space-y-2">
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">
              Items ({order.items.reduce((s, i) => s + i.quantity, 0)} pcs)
            </p>
            {order.items.map((item, idx) => {
              // Resolve image from localStorage if item.image is missing
              let resolvedImage = item.image;
              if (!resolvedImage) {
                try {
                  const listed = JSON.parse(localStorage.getItem('listed_products') ?? '[]');
                  const match = listed.find((p: { id: number; name?: string; price?: number; thumbnail?: string; images?: string[] }) =>
                    p.id === item.id || (p.name === item.name && p.price === item.price)
                  );
                  if (match) resolvedImage = match.thumbnail || (match.images?.[0]) || '';
                } catch { /* ignore */ }
              }
              const productCode = `#${String(item.id).toUpperCase().slice(-6)}`;
              return (
                <div key={idx} className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
                  <div className="flex gap-0">
                    {/* Product Image — tall & full */}
                    <div className="w-24 h-28 shrink-0 bg-gray-100 relative overflow-hidden">
                      {resolvedImage ? (
                        <img
                          src={resolvedImage}
                          alt={item.name}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-t-shirt-line text-gray-300 text-3xl"></i>
                        </div>
                      )}
                      {/* Quantity badge on image */}
                      <div className="absolute top-1.5 left-1.5 bg-gray-900/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        ×{item.quantity}
                      </div>
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 p-3 min-w-0 flex flex-col justify-between">
                      {/* Name + Code */}
                      <div>
                        <p className="text-gray-900 text-sm font-bold leading-tight line-clamp-2">{item.name}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[10px] font-mono font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md">
                            {productCode}
                          </span>
                          {item.category && (
                            <span className="text-[10px] text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded-md">
                              {item.category}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Size + Color */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {item.size && (
                          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
                            <i className="ri-ruler-2-line text-gray-400 text-[10px]"></i>
                            <span className="text-gray-700 text-xs font-semibold">Size: {item.size}</span>
                          </div>
                        )}
                        {item.selectedColor?.name && (
                          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1">
                            <span
                              className="w-3 h-3 rounded-full border border-gray-200 shrink-0"
                              style={{ backgroundColor: item.selectedColor.hex || '#ccc' }}
                            />
                            <span className="text-gray-700 text-xs font-semibold">{item.selectedColor.name}</span>
                          </div>
                        )}
                      </div>

                      {/* Price row */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                        <div>
                          <span className="text-gray-500 text-[10px]">₹{item.price} × {item.quantity}</span>
                        </div>
                        <span className="text-gray-900 font-bold text-sm">₹{item.price * item.quantity}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Order total strip */}
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2">
              <div className="flex items-center gap-1.5">
                <i className="ri-wallet-3-line text-green-600 text-sm"></i>
                <span className="text-green-700 text-xs font-semibold">Total (COD)</span>
                {order.wallet_discount > 0 && (
                  <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                    -₹{order.wallet_discount} wallet
                  </span>
                )}
              </div>
              <span className="text-green-700 font-bold text-base">₹{order.final_amount}</span>
            </div>
          </div>

          {!showRejectForm ? (
            <div className="flex gap-3">
              <button
                onClick={() => onAccept(order)}
                className="flex-1 py-3 rounded-2xl bg-green-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line text-lg"></i>
                Accept
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                className="flex-1 py-3 rounded-2xl bg-red-50 text-red-600 border border-red-200 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line text-lg"></i>
                Reject
              </button>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 font-semibold text-sm mb-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500"></i>
                Reason for rejection
              </p>
              <div className="space-y-2 mb-3">
                {REJECT_REASONS.map(r => (
                  <button
                    key={r}
                    onClick={() => { setRejectReason(r); setError(''); }}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium border transition-all cursor-pointer ${
                      rejectReason === r
                        ? 'bg-red-50 border-red-400 text-red-700'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className={`mr-2 ${rejectReason === r ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                    {r}
                  </button>
                ))}
                <button
                  onClick={() => { setRejectReason('custom'); setError(''); }}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs font-medium border transition-all cursor-pointer ${
                    rejectReason === 'custom'
                      ? 'bg-red-50 border-red-400 text-red-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`mr-2 ${rejectReason === 'custom' ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                  Other reason...
                </button>
                {rejectReason === 'custom' && (
                  <textarea
                    value={customReason}
                    onChange={e => { setCustomReason(e.target.value); setError(''); }}
                    placeholder="Type your reason here..."
                    rows={2}
                    maxLength={200}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none resize-none"
                  />
                )}
              </div>
              {error && (
                <p className="text-red-500 text-xs mb-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i> {error}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowRejectForm(false); setRejectReason(''); setCustomReason(''); setError(''); }}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Back
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line mr-1"></i>
                  Confirm Reject
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
